export const heroContent = {
  headline: "A place to return to yourself",
  subheadline: "Move  |  Restore  |  Connect",
  tagline: "A place to feel good today and better for years to come",
  location: "Wanstead, London",
  cta: "Begin Your RE:SET"
};

export const conceptBlocks = [
  {
    id: "cafe",
    label: "01 — The Café",
    title: "Premium Lifestyle Café",
    description: "A calm social space designed for dwell time and nourishment. Thoughtfully sourced ingredients, house-made wellness tonics, and a menu that supports your longevity practice. This is where community gathers, quietly.",
    details: "Open daily from early morning through evening. A place to pause before or after your practice, or simply to be. Warm, unhurried, and intentionally designed for return visits.",
    link: "Explore the menu",
    image: "https://public.readdy.ai/ai/img_res/c93a7fde3e501354d0d4b16902b8a470.jpg"
  },
  {
    id: "pilates",
    label: "02 — Reformer Pilates",
    title: "Form-Focused Movement",
    description: "Eight to ten reformer beds. Controlled capacity. Expert instruction focused on alignment, breath, and sustainable strength. This is movement as medicine—precise, intentional, and designed for longevity.",
    details: "Classes are capped to ensure individual attention. We prioritize form over intensity, depth over speed. Members enjoy priority booking and consistent instructor relationships.",
    link: "View class schedule",
    image: "https://readdy.ai/api/search-image?query=Reformer%20pilates%20studio%20interior%20minimal%20design%20with%20reformer%20bed%20detail%20shot%20soft%20natural%20lighting%20warm%20neutral%20tones%20beige%20and%20grey%20palette%20calm%20spa-like%20atmosphere%20clean%20lines%20wooden%20floors%20premium%20wellness%20space%20editorial%20photography%20style%20serene%20and%20focused%20environment&width=800&height=600&seq=reset002&orientation=landscape"
  },
  {
    id: "thermal",
    label: "03 — Thermal Recovery",
    title: "Private Restoration Rituals",
    description: "Two private thermal suites, each with sauna, steam, and ice plunge. Book by the hour. Bring a friend or come alone. This is nervous system care—heat, cold, rest, repeat. A ritual for resilience.",
    details: "Completely private. Robes, towels, and premium amenities included. Pre and post-session herbal tonics available. This is recovery as a practice, not a luxury.",
    link: "Book a thermal session",
    image: "https://readdy.ai/api/search-image?query=Luxury%20spa%20thermal%20suite%20interior%20with%20soft%20steam%20rising%20warm%20ambient%20lighting%20natural%20stone%20surfaces%20wooden%20sauna%20details%20calm%20blue-grey%20tones%20minimal%20design%20premium%20wellness%20sanctuary%20editorial%20photography%20style%20serene%20atmosphere%20linen%20textures%20candles%20and%20soft%20shadows&width=800&height=600&seq=reset003&orientation=landscape"
  }
];

export const whyNuFeatures = [
  {
    icon: "ri-heart-pulse-line",
    title: "Longevity-Led",
    description: "Not trend-chasing. Not quick fixes. Sustainable practices designed for long-term wellbeing and vitality."
  },
  {
    icon: "ri-refresh-line",
    title: "Nervous System Reset",
    description: "Every experience is designed to regulate, restore, and renew your nervous system through intentional practice."
  },
  {
    icon: "ri-shield-check-line",
    title: "Private & Unrushed",
    description: "Controlled capacity across all experiences. No crowds, no rush. Just calm, intentional space for renewal."
  },
  {
    icon: "ri-time-line",
    title: "Designed for Return",
    description: "Membership-led model built around recurring rituals and habit formation. A sanctuary you return to, again and again."
  }
];

export const testimonials = [
  {
    quote: "RE:SET has become my weekly ritual. The thermal suites are transformative, and the café feels like a second living room. It's the calm I didn't know I needed.",
    author: "Sarah M.",
    location: "Wanstead",
    image: "https://readdy.ai/api/search-image?query=Portrait%20of%20calm%20woman%20in%20her%2040s%20wearing%20soft%20beige%20linen%20robe%20natural%20makeup%20serene%20expression%20warm%20natural%20lighting%20spa%20wellness%20environment%20muted%20color%20palette%20editorial%20portrait%20photography%20style%20peaceful%20and%20grounded%20aesthetic&width=400&height=533&seq=reset004&orientation=portrait"
  },
  {
    quote: "The reformer classes are exceptional. Small groups, expert instruction, and a space that actually feels premium. I've tried many studios—this is different.",
    author: "James K.",
    location: "Wanstead",
    image: "https://readdy.ai/api/search-image?query=Portrait%20of%20professional%20man%20in%20his%2040s%20wearing%20soft%20grey%20wellness%20robe%20calm%20expression%20natural%20lighting%20spa%20environment%20warm%20neutral%20tones%20editorial%20portrait%20photography%20style%20serene%20and%20confident%20aesthetic&width=400&height=533&seq=reset005&orientation=portrait"
  },
  {
    quote: "It's rare to find a place that feels both luxurious and genuinely welcoming. RE:SET is my sanctuary—I come for the thermal suites and stay for the community.",
    author: "Emma L.",
    location: "Snaresbrook",
    image: "https://readdy.ai/api/search-image?query=Portrait%20of%20elegant%20woman%20in%20her%2030s%20wearing%20cream%20linen%20robe%20peaceful%20expression%20soft%20natural%20lighting%20wellness%20spa%20setting%20warm%20beige%20tones%20editorial%20portrait%20photography%20style%20calm%20and%20sophisticated%20aesthetic&width=400&height=533&seq=reset006&orientation=portrait"
  }
];

export const aboutContent = {
  headline: "Calm. Grounded. Enduring.",
  intro: "RE:SET was created as a modern sanctuary for wellness, renewal, and longevity. We believe wellness isn't about trends or quick fixes—it's about sustainable practices, nervous system regulation, and long-term vitality.",
  philosophy: [
    "We're not a gym. We're not chasing the next wellness trend. RE:SET is a modern sanctuary designed for depth, consistency, and return.",
    "Our approach combines expert-led practices with genuine human connection. Premium service without the pretense. A place where recovery, clarity, and feeling good become part of your daily rhythm.",
    "This is wellness for the long game. Reformer Pilates for sustainable strength. Thermal recovery for nervous system care. A café that nourishes body and community.",
    "We're village-led, membership-focused, and intentionally small. Because true wellness isn't scalable—it's personal, private, and built on trust."
  ]
};

export const spaceContent = {
  cafe: {
    title: "The Café",
    subtitle: "Nourishment & Community",
    description: "Our café is the heart of RE:SET—a warm, unhurried space designed for dwell time and connection. Thoughtfully sourced ingredients, house-made wellness tonics, and a menu that supports your longevity practice.",
    features: [
      "Specialty coffee and house-blend teas",
      "Nutrient-dense breakfast and lunch options",
      "House-made wellness tonics and adaptogenic drinks",
      "Calm seating areas with natural light",
      "Open daily from early morning through evening"
    ]
  },
  pilates: {
    title: "Reformer Pilates Studio",
    subtitle: "Form-Focused Movement",
    description: "Eight to ten reformer beds in a calm, controlled environment. Our classes prioritize alignment, breath work, and sustainable strength building. This is movement as medicine.",
    features: [
      "Small class sizes (maximum 10 participants)",
      "Expert instructors with clinical backgrounds",
      "Focus on form, alignment, and breath",
      "Consistent instructor relationships for members",
      "Priority booking for members"
    ]
  },
  thermal: {
    title: "Private Thermal Suites",
    subtitle: "Restoration Rituals",
    description: "Two completely private thermal suites, each equipped with sauna, steam room, and ice plunge. Book by the hour for solo restoration or bring a friend. This is nervous system care through contrast therapy.",
    features: [
      "Completely private booking (no shared spaces)",
      "Sauna, steam, and ice plunge in each suite",
      "Premium robes, towels, and amenities included",
      "Pre and post-session herbal tonics",
      "Guided protocols for optimal recovery"
    ]
  }
};

export const membershipContent = {
  headline: "Join the RE:SET Community",
  intro: "RE:SET is membership-led by design. We believe wellness is built through recurring rituals, not one-off visits. Our memberships are designed for habit formation, priority access, and long-term commitment to your wellbeing.",
  benefits: [
    "Priority booking across all experiences",
    "Consistent instructor relationships",
    "Member-only events and workshops",
    "Discounted rates on thermal suite bookings",
    "Complimentary wellness consultations",
    "Access to our private member community"
  ],
  types: [
    {
      name: "Reformer Membership",
      description: "Unlimited reformer classes with priority booking and consistent instructor relationships."
    },
    {
      name: "Thermal Membership",
      description: "Regular thermal suite access with discounted hourly rates and flexible booking."
    },
    {
      name: "Wellness Membership",
      description: "Combined access to reformer classes and thermal suites with maximum flexibility."
    }
  ]
};

export const locationContent = {
  address: "RE:SET Wellness Sanctuary",
  street: "63 High Street, Wanstead",
  city: "London, E11 2AE",
  transport: "5 minutes from Wanstead Station (Central Line)",
  mapEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2479.123456789!2d0.0275!3d51.5775!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNTHCsDM0JzM5LjAiTiAwwrAwMSczkjkuMCJF!5e0!3m2!1sen!2suk!4v1234567890"
};